<template>
  <CContainer class="datatable-container">
    <Loader :is-processing="isLoading" />
    <CRow class="justify-content-center mt-2">
      <CAlert
        v-if="reqResponse.message"
        class="mt-1 w-100 mx-3"
        :color="reqResponse.type"
        :show="!!reqResponse.message"
      >
        {{ reqResponse.message }}
      </CAlert>
      <CCol
        md="12"
        class="d-flex justify-content-center align-items-center mb-5"
      >
        <h1 class="text-center">Pass Transparency</h1>
        <div class="d-flex align-items-center ms-4">
          <span class="fw-bold me-3">On</span>
          <CFormSwitch
            switch-type="reversed"
            class="transparency-switch mx-1"
            shape="pill"
            :checked="transparencyStatus"
            @update:model-value="toggleStatus()"
          />
          <span class="fw-bold ms-2">Off</span>
        </div>
        <HelpCenterButton classes="ms-4" content-key="pass_transparency" />
      </CCol>
      <CCol
        v-if="transparencyStatus"
        md="8"
        class="d-flex align-items-center justify-content-center"
      >
        <LazyLoadSelect
          class="pass-transparency-selector"
          :classes="[{ 'is-invalid': !isValidated }, 'pt-selector']"
          :is-multiple="true"
          :selected="selectedTransparency"
          type="adults"
          placeholder="Allow these users to keep Pass Transparency"
          @changed="handleChangeTransparency"
        >
          <template #footer>
            <div v-if="!isValidated" class="invalid-feedback">
              This field is required
            </div>
          </template>
        </LazyLoadSelect>
        <button
          class="ms-4 btn bg-gradient-blue text-white py-2 px-4 fw-bold"
          @click="create"
        >
          Allow
        </button>
      </CCol>
    </CRow>

    <CRow v-show="transparencyStatus" class="my-5 justify-content-center">
      <CCol md="8">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between"
        >
          <div
            v-for="(tab, index) in tableTabs"
            :key="index"
            class="table-tab"
            :class="{ active: tab.value === activeTableTab.value }"
          >
            {{ tab.label }}
          </div>
          <div class="actions d-flex align-items-center">
            <CLink
              class="lh-normal text-accent-blue-light"
              download="pass-transparencies.csv"
              target="_blank"
              @click="generateExport()"
            >
              <i class="fi flaticon-download me-1" />
              <span class="d-none d-md-inline-block">Download CSV</span>
            </CLink>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4"
            >
              <CInputGroup class="w-auto">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div><i class="flaticon-magnifying-glass"></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search"
                  v-model="globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div class="status-guides-container d-block">
          <per-page-selector :pagination="pagination" :action="setPerPage" />
        </div>

        <!-- End of modals -->
        <DataTable
          v-if="users"
          class="datatable bg-white"
          :items="transparencies"
          :fields="dataTableFields"
          :sorter="{ external: true }"
          column-filter
          :no-items-view="{
            noResults: 'No items available',
            noItems: 'No items available'
          }"
          @update:sorter-value="sortByColumn"
        >
          <template #last_name-filter>
            <input
              v-model="columnSearchKeys.last_name.value"
              placeholder="Search by last name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>
          <template #first_name-filter>
            <input
              v-model="columnSearchKeys.first_name.value"
              placeholder="Search by last name"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
            />
          </template>
          <template #actions-filter>
            <div />
          </template>

          <template #last_name="{ item }">
            <td>
              {{ item.last_name }}
            </td>
          </template>
          <template #first_name="{ item }">
            <td>
              {{ item.first_name }}
            </td>
          </template>

          <template #actions="{ item }">
            <td>
              <button
                class="px-2 py-1 rounded d-flex align-items-center border-0"
                id="pass-transparency-custom-btn"
                @click="remove(item)"
              >
                <i class="flaticon-cancel" />
                <span class="ms-1 text-uppercase fw-bold">Remove</span>
              </button>
            </td>
          </template>
        </DataTable>

        <Pagination
          v-if="pagination.pages && pagination.pages > 1 && !isLazyLoadingMode"
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="pagination.activePage"
          :pages="pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1" />
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2" />
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow" />
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron" />
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import Loader from "@/components/Loader"
import { mapGetters, mapState } from "vuex"
import HelpCenterButton from "../../components/HelpCenterButton"
import LazyLoadSelect from "../LazyLoadSelect"
import DataTableMixin from "@/mixins/DataTableMixin"
import PerPageSelector from "../PerPageSelector.vue"

export default {
  name: "PassTransparencyDataTable",
  components: {
    Loader,
    HelpCenterButton,
    LazyLoadSelect,
    PerPageSelector
  },
  mixins: [DataTableMixin],
  data() {
    return {
      isLoading: false,
      selectedTransparency: [],
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      tableTabs: [
        {
          label: "Allowed",
          value: "allowed"
        }
      ],
      activeTableTab: {
        value: "allowed"
      },
      dataTableFields: [
        { key: "last_name", label: "Last Name", type: "tabular" },
        { key: "first_name", label: "First Name", type: "tabular" },
        { key: "actions", label: "Actions", type: "tabular" }
      ],
      columnSearchKeys: {
        last_name: {
          value: "",
          column: ["user.last_name"],
          sortBy: "user.last_name"
        },
        first_name: {
          value: "",
          column: ["user.first_name"],
          sortBy: "user.first_name"
        }
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      isValidated: true
    }
  },
  computed: {
    ...mapGetters({
      users: "passes/transparencies",
      formSelectTransparencyUsers: "passes/formSelectTransparencyUsers"
    }),
    ...mapState({
      transparencyStatus: (state) => state.passes.transparencyStatus
    }),
    transparencies() {
      return this.users.data.map((e) => ({
        ...e,
        first_name: e.user.first_name,
        last_name: e.user.last_name
      }))
    }
  },
  mounted() {
    this.initLazyLoading()
    this.getTableData()
  },
  methods: {
    handleChangeTransparency(value) {
      this.selectedTransparency = value
      this.isValidated = this.selectedTransparency.length
    },
    toggleTab(tab) {
      this.tabs[tab] = !this.tabs[tab]
    },
    toggleStatus() {
      this.$store.dispatch("passes/toggleTransparency", {
        status: !this.transparencyStatus
      })
    },
    getTableData(page, noLoader, isLazyLoadingMode) {
      this.isLoading = !noLoader
      isLazyLoadingMode = true
      this.pagination.activePage = page ? page : 1
      this.$store
        .dispatch("passes/getTransparencies", {
          page: this.pagination.activePage,
          per_page: this.pagination.per_page.value
        })
        .then((response) => {
          const data = response.data.data
          if (isLazyLoadingMode) {
            // TODO: Fix
            this.$store.dispatch(
              "passes/PUSH_TRANSPARENCIES",
              data.transparency_users
            )
          } else {
            this.$store.commit(
              "passes/SET_TRANSPARENCIES",
              data.transparency_users
            )
          }
          const meta = response.data.data.transparency_users
          if (meta) {
            this.pagination.total = meta.total
            this.pagination.from = meta.from
            this.pagination.to = meta.to
            this.pagination.pages = Math.ceil(meta.total / meta.per_page)
            this.pagination.activePage = Number(this.pagination.activePage)
            this.pagination = JSON.parse(JSON.stringify(this.pagination))
          }
          this.isLoading = false
        })
        .catch(() => {
          this.isLoading = false
        })
    },
    remove(transparency) {
      if (confirm("Are you sure you want to delete this item?")) {
        this.$store
          .dispatch("passes/deleteTransparency", {
            transparency_id: transparency.id
          })
          .then(() => {
            this.getTableData()
          })
        this.getTableData()
      }
    },
    create() {
      this.isValidated = true
      if (!this.selectedTransparency.length) {
        this.isValidated = false
        return
      }
      const userIds = this.selectedTransparency.map((st) => st.value.id)
      this.isLoading = true
      this.$store
        .dispatch("passes/createTransparency", {
          user_ids: userIds
        })
        .then(() => {
          this.setSuccessResponse()
          this.getTableData()
          setTimeout(() => {
            this.selectedTransparency = []
            this.resetErrors()
          }, 500)
          this.isLoading = false
        })
        .catch((err) => {
          const response = err.response.data
          this.setErrorResponse(response.message, response.errors)
          this.isLoading = false
        })
    },
    generateExport() {
      this.$store.dispatch("passes/exportTransparencies").then((response) => {
        if (response.data) {
          this.downloadExport(response.data)
        }
      })
    },
    downloadExport(data) {
      const csvDataForExport = "data:text/csv;charset=utf-8," + data
      const encodedUri = encodeURI(csvDataForExport)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("target", "_blank")
      link.setAttribute("download", "pass_transparency.csv")
      document.body.appendChild(link)
      link.click()
    },
    setErrorResponse(message, errors) {
      this.reqResponse = {
        type: "danger",
        message: message ? message : "Someting went wrong!",
        errors: errors ? errors : {}
      }
    },
    setActivePage(page) {
      this.pagination.activePage = page
      this.getTableData(this.pagination.activePage)
      this.$helpers.scrollToTop()
    },
    setPerPage(option) {
      if (option) {
        this.isLazyLoadingMode = option.label === "All entries"
        this.pagination.per_page = option
        this.getTableData()
      }
    },
    setSuccessResponse(message) {
      this.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    },
    resetErrors() {
      this.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    },
    resetSearchQuery() {
      this.globalQuery = ""
      this.filterParams.search_query = ""
      this.columnSearchKeys = {
        last_name: {
          value: "",
          column: ["last_name"],
          sortBy: "last_name"
        },
        first_name: {
          value: "",
          column: ["first_name"],
          sortBy: "first_name"
        }
      }
    },
    initLazyLoading() {
      const body = document.getElementsByClassName("main-scroll-container")[0]
      if (body) {
        body.onscroll = () => {
          if (
            this.isLazyLoadingMode &&
            this.users &&
            this.users.length &&
            this.pagination.total > this.users.length
          ) {
            if (body.offsetHeight + body.scrollTop + 1 >= body.scrollHeight) {
              this.pagination.activePage = this.pagination.activePage + 1
              this.getTableData(this.pagination.activePage, false, true)
            }
          }
        }
      }
    }
  }
}
</script>
<style scoped>
.pass-transparency-selector {
  width: 380px;
}
.pt-selector {
  min-width: 150px;
}
</style>
